import { FC, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { createSearchParams, useNavigate } from "react-router-dom";
import { GPAY_MERCHANT_ID, NODE_ENV, PAYMENT_SERVER_URL, PROJECT_NAME } from "constants/env.const";
import { useAppData, useUserStore } from "providers/RootStoreProvider";
import { AppRoutes } from "enums/routes.enum";
import { IxopayFormData } from "components/Payment/shared/types";
import { handleAmpliLoaded } from "helpers/handleAmpliLoaded";
import { ampli } from "services/ampli";
import { GoogleIxopaySafeBtn, IPaymentInfo, TGoogleIxopayOptions } from "@kma-core/w2-w2w-payment-checkout";

interface Props {
    data: IxopayFormData;
}

export const GooglePayButton: FC<Props> = observer(({ data }) => {
    const navigate = useNavigate();
    const { ipInfo, flowOuter } = useAppData();
    const { user } = useUserStore();
    const { plan, declineQuery, successQuery } = data;

    const paymentInfo: IPaymentInfo = useMemo(
        () => ({
            environment: NODE_ENV as "production" | "development",
            apiBaseUrl: PAYMENT_SERVER_URL,
            projectName: PROJECT_NAME,
            planFetchParams: {
                planAmplitudeId: plan?.amplitudeId ?? "",
                countryCode: ipInfo?.country || "",
                isTest: false,
            },
            userFetchParams: {
                email: user?.email || "",
                userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "",
                isLegal: !flowOuter,
                amplitudeSessionId: user?.id || "",
                countryCode: ipInfo?.country || "",
                postalCode: ipInfo?.postal,
                city: ipInfo?.city,
                ip: ipInfo?.ip || "",
                state: ipInfo?.state,
            },
            ampliConfig: { ampli },
        }),
        [plan]
    );

    const options: TGoogleIxopayOptions = {
        secureFlowUrl: `${window.location.origin}${AppRoutes.SECURE_FLOW_RESULT}`,
        successCallback: () => {
            navigate({ pathname: AppRoutes.SUCCESS, search: createSearchParams({ ...successQuery, provider: "applepay" }).toString() });
        },
        failureCallback: () => {
            navigate({ pathname: AppRoutes.DECLINE, search: createSearchParams({ ...declineQuery, provider: "applepay" }).toString() });
        },
        merchantInfo: {
            merchantId: GPAY_MERCHANT_ID,
            merchantName: "Geomobile",
        },
        buttonProps: {
            style: {
                height: "56px",
                width: "100%",
            },
            buttonRadius: 8,
            buttonType: "plain",
            buttonSizeMode: "fill",
            onClick: () => handleAmpliLoaded(() => ampli.googlePayButtonClicked()),
        },
    };

    return <GoogleIxopaySafeBtn paymentInfo={paymentInfo} options={options} />;
});
